import moment from 'moment-timezone'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useForm, useWatch} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {
  mapCommentsToDisplayItems,
  mapTabValuesToDefinitionMaps,
  mapTabValuesToKpiOptions,
  mapTabValuesToRoleOptions
} from '../mappers'
import {
  FiltersFormValues,
  RoleFilterValue,
  SelectedComment,
  SlaCommentDisplayItem,
  SlaCommentRecord
} from '../types'
import {buildDateMoment, DEFAULT_END_MONTH, DEFAULT_START_MONTH} from '../utils'

import {useSlaKpiComments, useSlaKpiTabValues} from './api'

const defaultFilters: FiltersFormValues = {
  searchTerm: '',
  roleFilter: 'all' as RoleFilterValue,
  kpiFilter: 'all',
  dateRange: [null, null]
}

type UseSlaCommentsModalStateParams = {
  plantUpmId: string
  year: number
  timezone: string
}

const buildQueryRange = (
  dateRange: [Date | null, Date | null],
  normalizedRange: {start: number; end: number},
  timezone: string,
  year: number
) => {
  const [rangeStart, rangeEnd] = dateRange

  if (rangeStart || rangeEnd) {
    const startMoment = buildDateMoment(rangeStart ?? rangeEnd, timezone)
    const endMoment = buildDateMoment(rangeEnd ?? rangeStart, timezone)
    const candidates = [startMoment, endMoment].filter((value): value is moment.Moment =>
      Boolean(value)
    )

    if (candidates.length > 0) {
      const earlier = candidates.length === 1 ? candidates[0] : moment.min(...candidates)
      const later = candidates.length === 1 ? candidates[0] : moment.max(...candidates)

      return {
        from: earlier.clone().startOf('day').toISOString(),
        to: later.clone().endOf('day').toISOString()
      }
    }
  }

  const fromMoment = moment
    .tz({year, month: normalizedRange.start - 1, day: 1}, timezone)
    .startOf('day')
  const toMoment = moment
    .tz({year, month: normalizedRange.end - 1, day: 1}, timezone)
    .endOf('month')

  return {from: fromMoment.toISOString(), to: toMoment.toISOString()}
}

const getInitialDateRange = (year: number, timezone: string): [Date, Date] => {
  const startDate = moment.tz({year, month: DEFAULT_START_MONTH - 1, day: 1}, timezone).toDate()
  const endDate = moment
    .tz({year, month: DEFAULT_END_MONTH - 1, day: 1}, timezone)
    .endOf('month')
    .toDate()
  return [startDate, endDate]
}

export const useSlaCommentsModalState = ({
  plantUpmId,
  year,
  timezone
}: UseSlaCommentsModalStateParams) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const {control, setValue} = useForm<FiltersFormValues>({
    defaultValues: {
      ...defaultFilters,
      dateRange: getInitialDateRange(year, timezone)
    }
  })

  const searchTerm = useWatch({control, name: 'searchTerm'})
  const roleFilter = useWatch({control, name: 'roleFilter'})
  const kpiFilter = useWatch({control, name: 'kpiFilter'})
  const dateRange = useWatch({control, name: 'dateRange'})

  const [startMonth, setStartMonth] = useState(DEFAULT_START_MONTH)
  const [endMonth, setEndMonth] = useState(DEFAULT_END_MONTH)
  const [selectedComment, setSelectedComment] = useState<SelectedComment | null>(null)

  const {data: tabValues = []} = useSlaKpiTabValues({plantUpmId, year}, {staleTime: 5 * 60 * 1000})

  // Build definition maps for lookups
  const definitionMaps = useMemo(() => mapTabValuesToDefinitionMaps(tabValues), [tabValues])

  // Role filter options
  const roleOptions = useMemo(() => mapTabValuesToRoleOptions(tabValues, t), [tabValues, t])

  // KPI filter options (filtered by role)
  const kpiOptions = useMemo(
    () => mapTabValuesToKpiOptions(tabValues, roleFilter, t),
    [roleFilter, tabValues, t]
  )

  // Reset KPI filter when it's no longer available
  useEffect(() => {
    if (kpiFilter === 'all') {
      return
    }

    const stillAvailable = kpiOptions.some((option) => option.value === kpiFilter)
    if (!stillAvailable) {
      setValue('kpiFilter', 'all', {shouldDirty: false, shouldTouch: false, shouldValidate: false})
    }
  }, [kpiFilter, kpiOptions, setValue])

  // Update month range from date picker
  useEffect(() => {
    const [startDate, endDate] = dateRange

    if (!startDate && !endDate) {
      setStartMonth(DEFAULT_START_MONTH)
      setEndMonth(DEFAULT_END_MONTH)
      return
    }

    const startMoment = buildDateMoment(startDate ?? endDate, timezone)
    const endMoment = buildDateMoment(endDate ?? startDate, timezone)

    if (startMoment) {
      setStartMonth(startMoment.month() + 1)
    }

    if (endMoment) {
      setEndMonth(endMoment.month() + 1)
    }
  }, [dateRange, timezone])

  // Normalize month range
  const normalizedRange = useMemo(() => {
    const start = Math.min(startMonth, endMonth)
    const end = Math.max(startMonth, endMonth)

    return {start, end}
  }, [startMonth, endMonth])

  // Build query range
  const queryRange = useMemo(
    () => buildQueryRange(dateRange, normalizedRange, timezone, year),
    [dateRange, normalizedRange, timezone, year]
  )

  // Fetch comments
  const commentsQuery = useSlaKpiComments(
    {
      plantUpmId,
      tabType: roleFilter !== 'all' ? roleFilter : undefined,
      kpiDefinitionId: kpiFilter !== 'all' ? kpiFilter : undefined,
      from: queryRange.from,
      to: queryRange.to
    },
    {staleTime: 60 * 1000}
  )

  const comments = useMemo(
    () => (commentsQuery.data ?? []) as ReadonlyArray<SlaCommentRecord>,
    [commentsQuery.data]
  )

  const isCommentsLoading = commentsQuery.isLoading || commentsQuery.isFetching

  // Build display items
  const displayItems = useMemo(
    () =>
      mapCommentsToDisplayItems({
        comments,
        definitionMaps,
        kpiFilter,
        roleFilter,
        searchTerm,
        t,
        timezone,
        language: i18n.language,
        unknownKpiLabel: t('commentsModal.unknownKpi'),
        unknownUserLabel: t('commentsModal.unknownUser')
      }),
    [comments, definitionMaps, kpiFilter, roleFilter, searchTerm, t, timezone, i18n.language]
  )

  const handleOpenVersions = useCallback((item: SlaCommentDisplayItem) => {
    setSelectedComment({
      kpiValueId: item.kpiValueId,
      kpiName: item.kpiName,
      kpiUnit: item.kpiUnit
    })
  }, [])

  const handleCloseVersions = useCallback(() => {
    setSelectedComment(null)
  }, [])

  return {
    // Form
    control,

    // Filter options
    roleOptions,
    kpiOptions,

    // Comments data
    displayItems,
    isCommentsLoading,
    isCommentsError: commentsQuery.isError,

    // Selected comment for versions
    selectedComment,
    handleOpenVersions,
    handleCloseVersions,

    // Translation
    t
  }
}
